"use client";

import { PortableText } from "next-sanity";
import { TextBlock as TextBlockType } from "../../../sanity/lib/types/textBlock";
import { LinkElement } from "../link/link";
import { PortableTextComponents } from "../portableText/PortableTextComponents";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useRef } from "react";

gsap.registerPlugin(ScrollTrigger);
gsap.registerPlugin(useGSAP);

export const TextBlock = ({
    data,
}: {
    data: TextBlockType & { maxWidth?: string; margin?: boolean };
}) => {
    const spacing = [];
    const ref = useRef<HTMLDivElement>(null);

    const cleanPosition = data.position;
    const cleanSpacing = data.spacing;
    const cleanSpacingTop = data.customSpacingTop;
    const cleanSpacingBottom = data.customSpacingBottom;
    const cleanedTitleSize = data.titleSize;

    if (cleanSpacing === "small") {
        spacing.push("py-20");
    } else if (cleanSpacing === "large") {
        spacing.push("py-24 md:py-60");
    }

    if (cleanSpacing === "custom") {
        spacing.push("md:pt-[var(--custom-spacing-top)]");
        spacing.push("pt-[var(--custom-spacing-top-mobile)]");
        spacing.push("md:pb-[var(--custom-spacing-bottom)]");
        spacing.push("pb-[var(--custom-spacing-bottom-mobile)]");
    }

    useGSAP(
        () => {
            let mm = gsap.matchMedia();

            mm.add("(min-width: 768px)", () => {
                let scrollTriggerSettings;

                scrollTriggerSettings = {
                    trigger: ".text-block",
                    start: "top 70%",
                    toggleActions: "play none none none",
                };

                const animateElements = gsap.utils.toArray(".gsap-animate");

                gsap.set(animateElements, { y: 50, opacity: 0 });
                gsap.to(animateElements, {
                    y: 0,
                    opacity: 1,
                    scrollTrigger: scrollTriggerSettings,
                    stagger: 0.1,
                });

                ScrollTrigger.refresh();
            });

            return () => {
                ScrollTrigger.refresh();
                const t = ScrollTrigger.getAll();
                t.forEach((t) => t.kill());
            };
        },
        { scope: ref },
    );

    return (
        <section
            style={
                {
                    "--custom-spacing-top": cleanSpacingTop?.desktop + "px",
                    "--custom-spacing-bottom": cleanSpacingBottom?.desktop + "px",
                    "--custom-spacing-top-mobile": cleanSpacingTop?.mobile + "px",
                    "--custom-spacing-bottom-mobile": cleanSpacingBottom?.mobile + "px",
                } as React.CSSProperties
            }
            className={`container ${spacing.join(" ")}`}
            ref={ref}
        >
            <div className={`${data.margin ? "mx-2.5 md:mx-5" : ""} text-block`}>
                {data.type === "2Col" ? (
                    <div className="mb-10">
                        <h2 className={`m-headline-2 gsap-animate md:d-${cleanedTitleSize}`}>
                            {data.title}
                        </h2>
                    </div>
                ) : (
                    <div className={`grid grid-cols-4 md:grid-cols-12 gap-2.5 md:gap-5`}>
                        <div
                            className={`${cleanPosition === "center" ? "col-span-4 md:col-span-12 flex justify-center" : "col-span-10"} ${cleanPosition === "right" ? "md:col-start-4" : "md:col-start-1"}`}
                        >
                            <div
                                className={`flex flex-col col-span-1 gap-4 md:gap-6 ${data.maxWidth}`}
                            >
                                {data.overline && (
                                    <h2 className="m-body-l md:d-body-l gsap-animate ">
                                        {data.overline}
                                    </h2>
                                )}
                                <div
                                    className={`m-headline-3 gsap-animate md:d-${cleanedTitleSize} whitespace-break-spaces`}
                                >
                                    <PortableText
                                        value={data.title}
                                        components={PortableTextComponents}
                                    />
                                </div>
                            </div>

                            {data.link && data.link.label && (
                                <div className="flex justify-start mt-14 gsap-animate">
                                    <LinkElement className="link" link={data.link} />
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
};
